import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import {
  FilterModel,
  IServerSideDatasource,
  IServerSideGetRowsParams,
} from 'ag-grid-community';

import { createOutsideEmbryoOptions } from '@/services/embryo-service/hooks';
import { addGridQueryParams } from '@/utils/query';

import { EmbryoFilters } from './types';

export const useOutsideEmbryoDataSource = (
  filters: EmbryoFilters,
  setLoading: (loading: boolean) => void,
) => {
  const queryClient = useQueryClient();
  const filtersRef = useRef(filters);
  const setLoadingRef = useRef(setLoading);

  useEffect(() => {
    filtersRef.current = filters;
    setLoadingRef.current = setLoading;
  }, [filters, setLoading]);

  const getRows = useCallback(
    (params: IServerSideGetRowsParams) => {
      if (
        typeof params.request.startRow !== 'number' ||
        typeof params.request.endRow !== 'number'
      ) {
        return;
      }

      const currentFilters = filtersRef.current;
      const currentSetLoading = setLoadingRef.current;

      currentSetLoading(true);

      const searchParams = new URLSearchParams();

      if (currentFilters.searchTerm) {
        searchParams.set('search', currentFilters.searchTerm);
      }
      if (currentFilters.stateCode != null) {
        searchParams.set('stateCode', String(currentFilters.stateCode));
      }

      const sortModel =
        params.request.sortModel.length > 0 ? params.request.sortModel : [];

      queryClient
        .fetchQuery({
          ...createOutsideEmbryoOptions(
            addGridQueryParams(
              searchParams,
              params.request.filterModel as FilterModel | null,
              sortModel,
              params.request.startRow,
              params.request.endRow,
            ),
          ),
        })
        .then((response) => {
          params.success({
            rowData: response.items,
            rowCount: response.totalCount,
          });
          currentSetLoading(false);
        })
        .catch((_error) => {
          params.fail();
          currentSetLoading(false);
        });
    },
    [queryClient],
  );

  const dataSource: IServerSideDatasource = useMemo(
    () => ({ getRows }),
    [getRows],
  );

  return { dataSource };
};
